'use client';

import { Divider, Stack, Typography } from '@mui/material';

interface AnomalyChartDataPoint {
  timestamp: string;
  actual: number;
  forecast: number;
  upperBound: number;
  lowerBound: number;
  isAnomaly: boolean;
  snapshotId: string;
}

interface AnomalyChartTooltipProps {
  payload?: readonly {
    dataKey?: string | number | ((obj: Record<string, unknown>) => unknown);
    payload?: AnomalyChartDataPoint;
  }[];
}

const TooltipRow = ({
  label,
  value,
  fontWeight,
}: {
  label: string;
  value: string;
  fontWeight?: number;
}) => (
  <Stack direction='row' justifyContent='space-between' gap='16px'>
    <Typography variant='subtitle2' color='text.secondary'>
      {label}
    </Typography>
    <Typography variant='subtitle2' fontWeight={fontWeight}>
      {value}
    </Typography>
  </Stack>
);

export const AnomalyChartTooltip = ({ payload }: AnomalyChartTooltipProps) => {
  const entries = payload ?? [];
  const raw =
    entries.find((e) => e.dataKey === 'actual')?.payload ?? entries[0]?.payload;
  if (!raw) return null;

  return (
    <Stack
      sx={{
        boxShadow: 'var(--mui-customShadows-xs)',
        borderColor: 'var(--mui-palette-divider)',
        background: 'var(--mui-palette-background-paper)',
        borderRadius: '6px',
        padding: '10px 14px',
        minWidth: '200px',
      }}
      gap='6px'
    >
      <Typography variant='subtitle2' sx={{ fontWeight: 500 }}>
        {raw.timestamp}
      </Typography>
      {raw.snapshotId && (
        <Typography
          variant='subtitle2'
          color='text.secondary'
          sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
        >
          {raw.snapshotId}
        </Typography>
      )}
      <Divider />
      <Stack gap='2px'>
        <TooltipRow
          label='Actual'
          value={raw.actual.toFixed(1)}
          fontWeight={600}
        />
        <TooltipRow label='Forecast' value={raw.forecast.toFixed(1)} />
        <TooltipRow label='Upper bound' value={raw.upperBound.toFixed(1)} />
        <TooltipRow label='Lower bound' value={raw.lowerBound.toFixed(1)} />
      </Stack>
      {raw.isAnomaly && (
        <>
          <Divider />
          <Typography
            variant='subtitle2'
            sx={{ fontWeight: 500 }}
            color='var(--mui-palette-error-main)'
          >
            ⚠ Anomaly detected
          </Typography>
        </>
      )}
    </Stack>
  );
};
